import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { Bell, Music4, Clock, ClipboardCheck, X } from "lucide-react";
import { useDataVersion } from "../hooks/useAppShell";

// Track seen notification IDs (not timestamps) — future-dated items like
// upcoming releases would otherwise keep the badge lit forever.
const SEEN_IDS_KEY = "ye_notifs_seen_ids";

const KIND_META = {
  new_demo:         { color: "var(--mint)",   Icon: Music4,         label: "New Demo" },
  pending_demo:     { color: "var(--warn)",   Icon: ClipboardCheck, label: "Awaiting Review" },
  upcoming_release: { color: "var(--violet)", Icon: Clock,          label: "Upcoming Release" },
};

function readSeenIds() {
  try {
    const raw = localStorage.getItem(SEEN_IDS_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw));
  } catch { return new Set(); }
}
function writeSeenIds(set) {
  try { localStorage.setItem(SEEN_IDS_KEY, JSON.stringify(Array.from(set))); }
  catch (err) { console.warn("localStorage:", err); }
}

function timeAgo(iso) {
  try {
    const t = new Date(iso).getTime();
    if (!t) return "";
    const diff = Date.now() - t;
    if (diff < 0) return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
    if (diff < 60_000) return "just now";
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
    if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return ""; }
}

export default function NotificationsBell({ variant = "desktop" }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [seenIds, setSeenIds] = useState(readSeenIds);
  const globalVersion = useDataVersion();
  const rootRef = useRef(null);
  const navigate = useNavigate();

  const load = () => {
    api.notifications()
      .then(r => setItems(r.items || []))
      .catch((err) => console.error("notifications load failed:", err));
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 60_000);
    return () => clearInterval(iv);
  }, []);
  useEffect(() => { if (globalVersion > 0) load(); }, [globalVersion]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const unread = items.filter(it => !seenIds.has(it.id)).length;

  const openPanel = () => {
    setOpen(true);
    // Mark ALL currently visible items as seen regardless of their `at` timestamp
    const next = new Set(seenIds);
    items.forEach(it => next.add(it.id));
    // Cap seen ID list to last ~500 to prevent unbounded localStorage growth
    if (next.size > 500) {
      const arr = Array.from(next).slice(-500);
      const capped = new Set(arr);
      setSeenIds(capped);
      writeSeenIds(capped);
    } else {
      setSeenIds(next);
      writeSeenIds(next);
    }
  };

  const clickItem = (item) => {
    setOpen(false);
    navigate(item.link);
  };

  const bellButton = (
    <button
      onClick={() => (open ? setOpen(false) : openPanel())}
      data-testid={`notif-bell-${variant}`}
      aria-label={`Notifications${unread ? ` (${unread} unread)` : ""}`}
      title={unread ? `${unread} unread notification${unread === 1 ? "" : "s"}` : "Notifications"}
      className="btn"
      style={{
        position: "relative",
        padding: variant === "mobile" ? 8 : "6px 10px",
        borderRadius: 999,
        background: "transparent",
        border: variant === "mobile" ? "1px solid var(--border)" : undefined,
        color: "var(--text-dim)",
        display: "inline-flex",
      }}
    >
      <Bell size={variant === "mobile" ? 14 : 13} />
      {unread > 0 && (
        <span
          data-testid={`notif-unread-badge-${variant}`}
          style={{
            position: "absolute", top: -4, right: -4,
            background: "var(--danger)", color: "#fff",
            borderRadius: 999, padding: "1px 5px",
            fontSize: 9, fontWeight: 700,
            minWidth: 15, textAlign: "center",
            border: "2px solid var(--panel)",
            fontFamily: "Space Grotesk",
          }}
        >
          {unread > 99 ? "99+" : unread}
        </span>
      )}
    </button>
  );

  return (
    <div ref={rootRef} style={{ position: "relative", display: "inline-block", zIndex: 100 }}>
      {bellButton}
      {open && (
        <div
          data-testid={`notif-panel-${variant}`}
          style={{
            position: "absolute",
            right: 0,
            top: "calc(100% + 8px)",
            width: variant === "mobile" ? "min(340px, calc(100vw - 24px))" : 380,
            maxHeight: 480,
            background: "var(--panel)",
            border: "1px solid var(--border)",
            borderRadius: 14,
            boxShadow: "var(--shadow-3)",
            // Very high z-index so the panel sits above KPI cards / other page
            // content — those establish their own stacking contexts and would
            // otherwise intercept clicks on our items.
            zIndex: 10000,
            overflow: "hidden",
            display: "flex", flexDirection: "column",
            isolation: "isolate",
          }}
        >
          <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700 }}>Notifications</div>
              <div className="muted" style={{ fontSize: 10.5, marginTop: 2 }}>{items.length} recent</div>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close" data-testid="notif-close" style={{ background: "transparent", border: "none", color: "var(--muted)", cursor: "pointer", padding: 4 }}>
              <X size={14} />
            </button>
          </div>
          <div style={{ overflowY: "auto", flex: 1 }}>
            {items.length === 0 ? (
              <div className="muted" style={{ padding: "28px 16px", textAlign: "center", fontSize: 12.5 }} data-testid="notif-empty">
                All caught up 🎉<br />No new demos, releases, or pending reviews.
              </div>
            ) : (
              items.map((it) => {
                const meta = KIND_META[it.kind] || { color: "var(--muted)", Icon: Bell, label: it.kind };
                const isNew = !seenIds.has(it.id);
                return (
                  <button
                    key={it.id}
                    type="button"
                    onClick={() => clickItem(it)}
                    data-testid={`notif-item-${it.id}`}
                    style={{
                      display: "flex", gap: 12, alignItems: "flex-start",
                      width: "100%", textAlign: "left", background: isNew ? "rgba(124,108,255,0.06)" : "transparent",
                      border: "none", borderBottom: "1px solid var(--border)",
                      padding: "12px 16px", cursor: "pointer", color: "var(--text)",
                    }}
                  >
                    <span style={{ marginTop: 2, color: meta.color, flexShrink: 0 }}>
                      <meta.Icon size={14} />
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.4 }}>{it.title}</div>
                      <div className="muted" style={{ fontSize: 11, marginTop: 3, lineHeight: 1.45 }}>{it.subtitle}</div>
                    </div>
                    <span className="muted" style={{ fontSize: 10, whiteSpace: "nowrap", marginLeft: 6 }}>{timeAgo(it.at)}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
